/**
 * Node estimate — a rough duration entered in one gesture (see
 * docs/internal/2026-09-06-execution-model-design.md §2).
 *
 * Shape on the node: `{ value: number, unit: "days" | "weeks" | "months" }`
 * or absent. The unit keeps the user's own granularity; `estimateToDays`
 * gives a single number for load and overrun computations.
 */
import { t } from "../../shared/i18n/index.js";

// Labels are getters: the language loads after this module is imported.
export const ESTIMATE_UNITS = {
  days: { get label() { return t("sync.estimate.days"); }, min: 1, max: 7, days: 1 },
  weeks: { get label() { return t("sync.estimate.weeks"); }, min: 1, max: 4, days: 7 },
  months: { get label() { return t("sync.estimate.months"); }, min: 1, max: 12, days: 30 },
};

/**
 * Validate a raw estimate (from CRDT, import or UI). Returns a clean
 * `{ value, unit }` or null. Values outside the slider range are kept as long
 * as they are positive integers — the slider is a convenience, not a limit.
 */
export function normalizeEstimate(raw) {
  if (!raw || typeof raw !== "object") return null;
  const unit = typeof raw.unit === 'string' && Object.hasOwn(ESTIMATE_UNITS, raw.unit) ? raw.unit : null;
  if (typeof raw.value !== 'number' && typeof raw.value !== 'string') return null;
  const value = Number(raw.value);
  if (!unit || !Number.isSafeInteger(value) || value < 1) return null;
  return { value, unit };
}

/** Estimate expressed in days (weeks = 7, months = 30); null when absent. */
export function estimateToDays(estimate) {
  const e = normalizeEstimate(estimate);
  return e ? e.value * ESTIMATE_UNITS[e.unit].days : null;
}

/** "3 days", "1 week", "2 months"; "" when absent. */
export function formatEstimate(estimate) {
  const e = normalizeEstimate(estimate);
  if (!e) return "";
  return t(`sync.estimate.value.${e.unit}`, { count: e.value });
}
